import { Component, OnDestroy, AfterViewInit, ElementRef, ViewChild, Renderer2, ChangeDetectorRef, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { AppService } from '../@lcp-framework/service/common/app.service';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from './sidebar';
import { ThemeCustomizerComponent } from './theme-customizer';
import { HeaderComponent } from './header';
import { CommonSharedModule } from '../@lcp-framework/shared/common/common.module';
import { FormsModule } from '@angular/forms';
import { initialState } from '../store/index.reducer';
import { environment } from '../../environments/environment';
import { LocalStorageService } from '../@lcp-framework/service/common/local-storage.service';
import { OpenaiService } from '../@lcp-framework/service/common/openai.service';
import { LayoutReadyService } from '../@lcp-framework/service/common/layout-ready.service';
import { LanguageService } from '../@lcp-framework/service/common/language.service';
import { race, Subscription, take, timer } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app-layout.html',
  standalone: true,
  imports: [CommonSharedModule, RouterModule, SidebarComponent, ThemeCustomizerComponent, HeaderComponent, TranslateModule, FormsModule],
})
export class AppLayout implements AfterViewInit, OnDestroy {
  @ViewChild('pullRefreshIndicator') pullRefreshIndicatorRef?: ElementRef<HTMLElement>;

  // Pull-to-refresh, native app only - a browser tab already has its own
  // overscroll behavior (and no finger gesture to hijack in the first
  // place on desktop), so this only wires up inside the Capacitor shell.
  pullToRefreshEnabled = Capacitor.isNativePlatform();
  private pullStartY = 0;
  private isPulling = false;
  private isRefreshingViaPull = false;
  private readonly PULL_THRESHOLD = 70;
  private readonly PULL_MAX = 110;
  // Bound once so add/removeEventListener reference the same function.
  private readonly onPullTouchStart = (event: TouchEvent) => this.handlePullTouchStart(event);
  private readonly onPullTouchMove = (event: TouchEvent) => this.handlePullTouchMove(event);
  private readonly onPullTouchEnd = () => this.handlePullTouchEnd();

  store: any = initialState;
  isLoading = true;
  showTopButton = false;
  apiUrl = localStorage.getItem('lcp_api_base_url') || environment.apiUrl;
  enableVoiceSearch = false;
  private menuReadySub?: Subscription;
  private languageReadySub?: Subscription;
  private isMenuReady = false;
  private isLanguageReady = false;

  constructor(
    private renderer: Renderer2,
    public translate: TranslateService,
    public storeData: Store<any>,
    private service: AppService,
    private router: Router,
    private localstore: LocalStorageService,
    private openaiService: OpenaiService,
    private layoutReadyService: LayoutReadyService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    // Reset ready state so loader shows on each fresh navigation to app layout
    this.layoutReadyService.reset();
  }
  headerClass = '';

  mediaRecorder: any;
  audioChunks: any[] = [];
  audioBlob: Blob | null = null;
  isRecording = false;
  transcribedText: string = '';
  isTranscribing = false;
  transcribingText: string = 'Transcribing';
  displayedText: string = '';
  fullTranscribedText: string = '';
  hasTranscriptionData: boolean = false;
  micHoverText: string = 'Tap to hold & record';
  showTooltip: boolean = false;
  isPreviewOpen: boolean = false;
  isHolding: boolean = false;
  holdTimer: any;

  private isConfigFlagEnabled(value: unknown): boolean {
    return value === true || value === 'true';
  }

  ngOnInit() {
    this.initStore();
    const apiUrl = localStorage.getItem('lcp_api_base_url') || environment.apiUrl;
    const configRaw = this.localstore.getData('config');
    if (configRaw) {
      try {
        const config = JSON.parse(configRaw);
        this.enableVoiceSearch = this.isConfigFlagEnabled(config?.enable_voice_search);
        if (config?.favicon) {
          this.changeFavicon(apiUrl + '/' + config.favicon);
        }
      } catch (error) {
        this.enableVoiceSearch = false;
      }
    }

    // Wait for both the sidebar menu and the lang_contents refresh before hiding the loader
    this.menuReadySub = this.layoutReadyService.menuReady.subscribe((ready) => {
      if (ready) {
        this.isMenuReady = true;
        this.tryHideLoader();
      }
    });

    // header.ts triggers the lang_contents fetch; wait for it (or a timeout fallback so
    // the loader can't get stuck) before revealing the dashboard.
    this.languageReadySub = race(this.languageService.getLanguageDataUpdates().pipe(take(1)), timer(8000)).subscribe(() => {
      this.isLanguageReady = true;
      this.tryHideLoader();
    });

    this.initAnimation();
    this.startTypingAnimation();
    window.addEventListener('scroll', () => {
      const show = document.body.scrollTop > 50 || document.documentElement.scrollTop > 50;
      if (this.showTopButton !== show) {
        setTimeout(() => {
          if (!this.menuReadySub?.closed) {
            this.showTopButton = show;
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  ngAfterViewInit() {
    if (!this.pullToRefreshEnabled) return;
    // touchmove needs passive:false so preventDefault() can actually stop the
    // page's own scroll/bounce while the indicator is being dragged down -
    // Angular's (touchmove) template binding doesn't give us that control,
    // so these are wired up directly instead.
    window.addEventListener('touchstart', this.onPullTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onPullTouchMove, { passive: false });
    window.addEventListener('touchend', this.onPullTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.onPullTouchEnd, { passive: true });
  }

  ngOnDestroy() {
    this.menuReadySub?.unsubscribe();
    this.languageReadySub?.unsubscribe();
    window.removeEventListener('scroll', () => {});
    if (this.pullToRefreshEnabled) {
      window.removeEventListener('touchstart', this.onPullTouchStart);
      window.removeEventListener('touchmove', this.onPullTouchMove);
      window.removeEventListener('touchend', this.onPullTouchEnd);
      window.removeEventListener('touchcancel', this.onPullTouchEnd);
    }
  }

  private currentScrollTop(): number {
    return Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
  }

  private handlePullTouchStart(event: TouchEvent): void {
    if (this.isRefreshingViaPull || this.currentScrollTop() > 0) return;
    this.pullStartY = event.touches[0].clientY;
    this.isPulling = true;
  }

  private handlePullTouchMove(event: TouchEvent): void {
    if (!this.isPulling || this.isRefreshingViaPull) return;
    const delta = event.touches[0].clientY - this.pullStartY;
    if (delta <= 0 || this.currentScrollTop() > 0) {
      this.isPulling = false;
      this.setPullIndicator(0);
      return;
    }
    // Resistance curve - each extra pixel of finger movement yields less
    // visual travel the further the user pulls, matching the rubbery feel
    // of a native pull-to-refresh instead of the indicator tracking 1:1.
    const distance = Math.min(this.PULL_MAX, delta * 0.5);
    this.setPullIndicator(distance);
    if (distance > 4 && event.cancelable) {
      event.preventDefault();
    }
  }

  private handlePullTouchEnd(): void {
    if (!this.isPulling) return;
    this.isPulling = false;
    const el = this.pullRefreshIndicatorRef?.nativeElement;
    const distance = el ? parseFloat(el.style.getPropertyValue('--pull-distance') || '0') : 0;
    if (distance >= this.PULL_THRESHOLD) {
      this.isRefreshingViaPull = true;
      this.setPullIndicator(this.PULL_THRESHOLD, true);
      // Brief pause so the spinner is actually visible before the reload
      // tears everything down, rather than an instant flash.
      setTimeout(() => window.location.reload(), 350);
    } else {
      this.setPullIndicator(0);
    }
  }

  // Drives the indicator directly via the DOM instead of Angular bindings +
  // change detection - this fires on every touchmove (~30-60/sec), and a
  // full CD pass that often isn't worth it for a single transform/opacity
  // update on one element.
  private setPullIndicator(distance: number, spinning = false): void {
    const el = this.pullRefreshIndicatorRef?.nativeElement;
    if (!el) return;
    el.style.setProperty('--pull-distance', String(distance));
    el.style.transform = `translate(-50%, ${distance - 50}px)`;
    el.style.opacity = distance > 0 ? '1' : '0';
    const icon = el.querySelector<HTMLElement>('.pull-refresh-icon');
    const spinner = el.querySelector<HTMLElement>('.pull-refresh-spinner');
    if (icon) {
      icon.style.display = spinning ? 'none' : '';
      icon.style.transform = distance >= this.PULL_THRESHOLD ? 'rotate(180deg)' : 'rotate(0deg)';
    }
    if (spinner) {
      spinner.style.display = spinning ? '' : 'none';
    }
  }

  private tryHideLoader(): void {
    if (!this.isMenuReady || !this.isLanguageReady) {
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = false;
      this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });
      this.cdr.detectChanges();
    });
  }

  initAnimation() {
    this.service.changeAnimation();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.service.changeAnimation();
      }
    });

    const ele: HTMLElement | null = document.querySelector('.animation');
    ele?.addEventListener('animationend', () => {
      this.service.changeAnimation('remove');
    });
  }

  changeFavicon(url: any): void {
    const favicon = this.renderer.selectRootElement('#common-favicon', true);
    this.renderer.setAttribute(favicon, 'href', url);
  }

  initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  goToTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  startTypingAnimation() {
    let dots = 0;
    setInterval(() => {
      if (this.isTranscribing) {
        dots = (dots + 1) % 4;
        this.transcribingText = 'Transcribing' + '.'.repeat(dots);
      }
    }, 2000);
  }

  typeWordByWord(text: string) {
    this.fullTranscribedText = text;
    this.displayedText = '';
    const words = text.split(' ');
    let currentWordIndex = 0;

    const typeNextWord = () => {
      if (currentWordIndex < words.length) {
        if (currentWordIndex > 0) {
          this.displayedText += ' ';
        }
        this.displayedText += words[currentWordIndex];
        currentWordIndex++;
        setTimeout(typeNextWord, 1100); // Adjust speed as needed
      }
    };

    typeNextWord();
  }

  stopRecordingAndSend() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.onstop = () => {
        this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.isRecording = false;
        // Automatically send the audio when recording stops
        this.sendAudio();
      };
      this.mediaRecorder.stop();
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
    }
  }

  togglePreview() {
    this.isPreviewOpen = !this.isPreviewOpen;
    if (!this.isPreviewOpen) {
      this.closeTranscriptionPreview();
    }
  }

  startHold() {
    this.isHolding = true;
    this.holdTimer = setTimeout(() => {
      if (this.isHolding) {
        this.startRecording();
        this.micHoverText = 'Release to translate';
      }
    }, 300); // Increased delay to better distinguish click from hold
  }

  endHold() {
    if (this.isHolding && !this.isRecording) {
      // this is the click, not a hold
      setTimeout(() => {
        if (!this.isRecording) {
          this.togglePreview();
        }
      }, 50);
    } else if (this.isRecording) {
      this.stopRecordingAndSend();
    }
    this.isHolding = false;
    clearTimeout(this.holdTimer);
  }

  handleMicClick(event: MouseEvent) {
    // Prevent default to avoid interference with mouse events
    event.preventDefault();
  }

  async startVoiceSearch() {
    if (!this.isRecording) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  async startRecording() {
    if (this.isRecording) return;
    this.isRecording = true;
    this.audioChunks = []; // Reset chunks for new recording
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    this.mediaRecorder = new MediaRecorder(stream);

    this.mediaRecorder.ondataavailable = (event: any) => {
      this.audioChunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
      this.isRecording = false;
    };

    this.mediaRecorder.start();
  }

  playAudio() {
    if (this.audioBlob) {
      const audioUrl = URL.createObjectURL(this.audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
    }
  }

  closeTranscriptionPreview() {
    this.displayedText = '';
    this.transcribedText = '';
    this.fullTranscribedText = '';
    this.isTranscribing = false;
    this.hasTranscriptionData = false;
    this.isPreviewOpen = false;
  }

  sendTranscribedText() {
    if (this.displayedText && this.displayedText.trim()) {
      this.openaiService.searchMenuTargetEmbeddings({ query: this.displayedText.trim() }).subscribe(
        (res) => {
          // Check if response is successful and has results
          if (res.code === 200 && res.status === true && res.data?.results?.length > 0) {
            const firstResult = res.data.results[0];
            const targetRoute = firstResult.target;

            // Navigate to the target route
            this.router.navigate([targetRoute]);

            // Close the transcription preview after navigation
            this.closeTranscriptionPreview();
          } else {
            console.warn('No results found or API error');
          }
        },
        (error) => {
          console.error('Search API error:', error);
        }
      );
    }
  }

  handleEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendTranscribedText();
    }
  }

  sendAudio() {
    if (this.audioBlob) {
      this.isTranscribing = true;
      this.displayedText = ''; // Clear previous text
      this.fullTranscribedText = ''; // Clear previous text
      const formData = new FormData();
      formData.append('audio', this.audioBlob, 'recording.wav');

      this.openaiService.transcribeAudio(formData).subscribe(
        (res) => {
          // Store the transcribed text and start word-by-word animation
          const transcribedResult = res.data?.transcription?.processedText || res.data?.transcription?.originalText || 'Transcription not available';
          this.transcribedText = transcribedResult;
          this.isTranscribing = false;
          this.hasTranscriptionData = true; // Set flag to keep preview open
          this.typeWordByWord(transcribedResult);
        },
        (error) => {
          console.error('Transcription error:', error);
          this.transcribedText = 'Transcription failed';
          this.displayedText = 'Transcription failed';
          this.isTranscribing = false;
        }
      );
    }
  }
}
